import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  TrendingUp,
  Briefcase,
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  IndianRupee,
  RefreshCw,
  Loader2,
  Trophy,
  Target,
  ArrowRight,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import { recruiterPerformanceApi, getErrorMessage } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { canSeeRevenue } from '../lib/perms';
import { toast } from 'sonner';
import DateRangePicker from '../components/DateRangePicker';

const STAGE_COLORS = {
  sourced: 'bg-slate-400',
  screening: 'bg-sky-400',
  client_submission: 'bg-violet-500',
  interview_scheduled: 'bg-amber-400',
  interview_selected: 'bg-emerald-400',
  offer_released: 'bg-emerald-600',
  joined: 'bg-teal-700',
};
const STAGE_LABELS = {
  sourced: 'Sourced',
  screening: 'Screening',
  client_submission: 'Client Sub',
  interview_scheduled: 'Interview',
  interview_selected: 'Selected',
  offer_released: 'Offered',
  joined: 'Joined',
};

const formatINR = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const num = Number(n);
  if (num >= 10000000) return `₹ ${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹ ${(num / 100000).toFixed(2)} L`;
  return `₹ ${num.toLocaleString('en-IN')}`;
};

const KpiTile = ({ label, value, icon: Icon, tone = 'slate', sub, testid }) => {
  const toneMap = {
    slate: 'bg-slate-50 border-slate-200',
    teal: 'bg-teal-50 border-teal-200',
    emerald: 'bg-emerald-50 border-emerald-200',
    amber: 'bg-amber-50 border-amber-200',
    rose: 'bg-rose-50 border-rose-200',
    violet: 'bg-violet-50 border-violet-200',
  };
  return (
    <div className={`rounded-lg border ${toneMap[tone]} px-3 py-2.5 flex items-start gap-3`} data-testid={testid}>
      <div className="w-9 h-9 rounded-md bg-white/70 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4.5 h-4.5 text-slate-700" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold truncate">{label}</div>
        <div className="text-2xl font-bold text-slate-900 leading-tight truncate">{value}</div>
        {sub ? <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div> : null}
      </div>
    </div>
  );
};

// ======================= Recruiter picker (managers only) =======================
// Lists ONLY users the caller is allowed to view performance for:
//   • Admin → whole org
//   • Delivery Manager / Team Lead → self + reports_to subtree
//   • Recruiter → just self (picker is hidden upstream anyway)
// Backed by GET /api/recruiters/visible so peers / out-of-subtree users never
// surface here — keeping the UI honest with the backend RBAC.
const RecruiterPicker = ({ value, onChange, currentName, meId }) => {
  const [open, setOpen] = useState(false);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || recs.length > 0) return;
    setLoading(true);
    recruiterPerformanceApi.visible()
      .then((res) => {
        const items = res.data?.items || [];
        // Drop the caller themselves — they get a dedicated "My performance"
        // row at the top of the list.
        setRecs(meId ? items.filter((u) => u.id !== meId) : items);
      })
      .catch(() => setRecs([]))
      .finally(() => setLoading(false));
  }, [open, recs.length, meId]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="rp-recruiter-picker"
          className="flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-800 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-200"
        >
          <Users className="w-3.5 h-3.5 text-slate-400" />
          <span className="truncate max-w-[180px]">{currentName || 'Select recruiter'}</span>
          <ArrowRight className="w-3 h-3 text-slate-400 rotate-90" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search team member…" data-testid="rp-recruiter-search" />
          <CommandList className="max-h-[320px]">
            {loading ? (
              <div className="flex items-center justify-center py-6 text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm">Loading…</span>
              </div>
            ) : (
              <>
                <CommandEmpty>No team members match.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="me"
                    keywords={['me', 'self', 'mine']}
                    onSelect={() => {
                      onChange('me');
                      setOpen(false);
                    }}
                    data-testid="rp-recruiter-option-me"
                  >
                    <Trophy className="w-3.5 h-3.5 mr-2 text-amber-500" />
                    <span className="font-semibold">My performance</span>
                  </CommandItem>
                </CommandGroup>
                <CommandGroup heading="Team">
                  {recs.map((u) => (
                    <CommandItem
                      key={u.id}
                      value={u.id}
                      keywords={[u.name || '', u.email || '']}
                      onSelect={() => {
                        onChange(u.id);
                        setOpen(false);
                      }}
                      data-testid={`rp-recruiter-option-${u.id}`}
                      className="flex items-center gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{u.name}</div>
                        <div className="text-[10px] text-slate-500 truncate">{u.email} · {u.role}</div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// ======================= Pipeline funnel (horizontal bars with %) =======================
const FunnelView = ({ funnel }) => {
  const top = funnel?.[0]?.count || 0;
  if (!funnel || funnel.length === 0 || top === 0) {
    return (
      <div className="text-center py-8 text-slate-400" data-testid="rp-funnel-empty">
        <Target className="w-6 h-6 mx-auto mb-2 text-slate-300" />
        <p className="text-sm">No pipeline data yet for this period.</p>
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid="rp-funnel">
      {funnel.map((f, idx) => {
        const pct = top > 0 ? (f.count / top) * 100 : 0;
        const conv = idx > 0 && funnel[idx - 1].count > 0
          ? Math.round((f.count / funnel[idx - 1].count) * 100)
          : null;
        return (
          <div
            key={f.stage}
            className="flex items-center gap-2"
            data-testid={`rp-funnel-stage-${f.stage}`}
          >
            <div className="w-20 text-[11px] text-slate-600 font-medium truncate text-right pr-1" title={STAGE_LABELS[f.stage]}>
              {STAGE_LABELS[f.stage]}
            </div>
            <div className="flex-1 h-7 bg-slate-100 rounded relative overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 ${STAGE_COLORS[f.stage]} flex items-center justify-end pr-2 text-[11px] font-semibold text-white transition-all`}
                style={{ width: `${Math.max(pct, 8)}%` }}
              >
                {f.count > 0 ? f.count : ''}
              </div>
            </div>
            <div className="w-12 text-right text-[10px] text-slate-500 font-mono">
              {conv != null ? `${conv}%` : ''}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const RecruiterPerformance = () => {
  const { user: me } = useAuth();
  const showRevenue = canSeeRevenue(me);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState(searchParams.get('period') || 'month');
  const [userId, setUserId] = useState(searchParams.get('user_id') || 'me');
  // Custom range from URL (?from=YYYY-MM-DD&to=YYYY-MM-DD) — overrides `period` when set
  const _initFrom = searchParams.get('from');
  const _initTo = searchParams.get('to');
  const [customRange, setCustomRange] = useState(
    _initFrom && _initTo ? { from: new Date(_initFrom), to: new Date(_initTo) } : null
  );
  const rangeIso = customRange
    ? {
        start_date: customRange.from.toISOString().slice(0, 10),
        end_date: customRange.to.toISOString().slice(0, 10),
      }
    : null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isMgr = useMemo(() => ['admin', 'delivery_manager', 'team_lead'].includes(me?.role), [me]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await recruiterPerformanceApi.get(userId, period, rangeIso);
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load performance data'));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Sync URL state
    const next = new URLSearchParams();
    if (userId !== 'me') next.set('user_id', userId);
    if (customRange) {
      next.set('from', customRange.from.toISOString().slice(0, 10));
      next.set('to', customRange.to.toISOString().slice(0, 10));
    } else if (period !== 'month') {
      next.set('period', period);
    }
    setSearchParams(next, { replace: true });
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, period, customRange]);

  // Single source of truth when the picker changes
  const handleRangeChange = ({ preset, customRange: cr }) => {
    if (cr && cr.from && cr.to) {
      setCustomRange(cr);
      return;
    }
    if (!preset) return;
    // Map quick presets onto the legacy enum where possible. Anything else
    // (week / mtd / ytd) becomes a custom range so we always pass through the
    // backend's start_date/end_date path.
    if (['month', 'quarter', 'year', 'all'].includes(preset)) {
      setPeriod(preset);
      setCustomRange(null);
      return;
    }
    const today = new Date();
    const from = new Date(today);
    if (preset === 'week') from.setDate(today.getDate() - 6);
    else if (preset === 'mtd') from.setDate(1);
    else if (preset === 'ytd') { from.setMonth(0); from.setDate(1); }
    from.setHours(0, 0, 0, 0);
    setCustomRange({ from, to: today });
  };

  const k = data?.kpis || {};
  const teamAvg = data?.team_avg || {};
  const subsPeriod = k.submissions_period || 0;
  const subsVsAvg = teamAvg.submissions && subsPeriod > 0
    ? Math.round((subsPeriod - teamAvg.submissions) / Math.max(teamAvg.submissions, 1) * 100)
    : null;

  return (
    <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-4 space-y-4" data-testid="recruiter-performance-page">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            {data?.recruiter?.name ? `${data.recruiter.name}'s Performance` : 'Performance'}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {data?.recruiter?.role ? <span className="capitalize">{data.recruiter.role.replace(/_/g, ' ')}</span> : ''}
            {data?.recruiter?.email ? <> · <span className="text-slate-600">{data.recruiter.email}</span></> : null}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isMgr && (
            <RecruiterPicker
              value={userId}
              currentName={data?.recruiter?.name}
              meId={me?.id}
              onChange={setUserId}
            />
          )}
          <DateRangePicker
            preset={customRange ? null : period}
            customRange={customRange}
            onChange={handleRangeChange}
            testid="rp-range"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="rp-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </header>

      {loading && !data ? (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span>Loading performance data…</span>
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5" data-testid="rp-kpi-strip">
            <KpiTile
              testid="rp-kpi-submissions"
              label="Submissions"
              value={k.submissions_period ?? 0}
              icon={Users}
              tone="slate"
              sub={subsVsAvg != null ? `${subsVsAvg >= 0 ? '+' : ''}${subsVsAvg}% vs team avg` : `Today: ${k.submissions_today ?? 0} · 7d: ${k.submissions_week ?? 0}`}
            />
            <KpiTile
              testid="rp-kpi-active"
              label="Active Pipeline"
              value={k.active_candidates ?? 0}
              icon={Target}
              tone="teal"
              sub="In sourced → offer"
            />
            <KpiTile
              testid="rp-kpi-closures"
              label="Closures"
              value={k.closures_period ?? 0}
              icon={CheckCircle2}
              tone="emerald"
              sub={teamAvg.closures != null ? `Team avg: ${teamAvg.closures}` : null}
            />
            <KpiTile
              testid="rp-kpi-conversion"
              label="Conversion"
              value={`${k.conversion_pct ?? 0}%`}
              icon={TrendingUp}
              tone="violet"
              sub="Submissions → Joined"
            />
            <KpiTile
              testid="rp-kpi-tat"
              label="Avg TAT"
              value={k.avg_tat_days != null ? `${k.avg_tat_days}d` : '—'}
              icon={Clock}
              tone="amber"
              sub="Sourced → Joined"
            />
            <KpiTile
              testid="rp-kpi-revenue"
              label="Candidate CTC"
              value={formatINR(k.candidate_ctc_total || 0)}
              icon={IndianRupee}
              tone="emerald"
              sub="Joined candidates' CTC"
            />
            <KpiTile
              testid="rp-kpi-jobs"
              label="Jobs Allocated"
              value={k.jobs_assigned ?? 0}
              icon={Briefcase}
              tone="slate"
              sub="Open requisitions"
            />
          </div>

          <div className="grid grid-cols-12 gap-4">
            {/* Pipeline funnel */}
            <Card className="col-span-12 lg:col-span-5 p-4" data-testid="rp-funnel-card">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-slate-800">Pipeline Funnel</h2>
                <span className="text-[10px] text-slate-500">{data?.period_label}</span>
              </div>
              <FunnelView funnel={data?.funnel} />
              <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Submissions</div>
                  <div className="text-sm font-bold text-slate-800">{k.submissions_period ?? 0}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Joined</div>
                  <div className="text-sm font-bold text-emerald-700">{k.closures_period ?? 0}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Rejected</div>
                  <div className="text-sm font-bold text-rose-600">{k.rejected_period ?? 0}</div>
                </div>
              </div>
            </Card>

            {/* Per-job breakdown */}
            <Card className="col-span-12 lg:col-span-7 flex flex-col" data-testid="rp-jobs-card">
              <div className="px-4 py-3 border-b flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-slate-500" />
                <h2 className="text-sm font-semibold text-slate-800">Per-Job Breakdown</h2>
                <span className="ml-auto text-xs font-mono text-slate-500">{data?.jobs?.length || 0}</span>
              </div>
              <ScrollArea className="flex-1 max-h-[420px]">
                {(data?.jobs?.length || 0) === 0 ? (
                  <div className="text-center py-12 text-sm text-slate-400" data-testid="rp-jobs-empty">
                    No applications submitted in this period.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">
                        <th className="text-left px-3 py-2">Job</th>
                        <th className="text-right px-2 py-2">Total</th>
                        <th className="text-right px-2 py-2">Active</th>
                        <th className="text-right px-2 py-2">Joined</th>
                        <th className="text-right px-2 py-2 hidden md:table-cell">Rejected</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.jobs.map((j, idx) => (
                        <tr
                          key={j.job_id}
                          className="hover:bg-slate-50/50 cursor-pointer transition"
                          onClick={() => navigate(`/workbench?job_id=${j.job_id}`)}
                          data-testid={`rp-job-row-${idx}`}
                        >
                          <td className="px-3 py-2.5">
                            <div className="font-semibold text-slate-900 truncate max-w-[260px]" title={j.job_title}>
                              {j.job_title || 'Untitled'}
                            </div>
                            <div className="text-xs text-slate-500 truncate max-w-[260px]" title={j.client_name}>
                              {j.client_name || '—'}
                            </div>
                          </td>
                          <td className="px-2 py-2.5 text-right text-xs font-semibold text-slate-700">{j.total}</td>
                          <td className="px-2 py-2.5 text-right text-xs text-teal-700">{j.active}</td>
                          <td className="px-2 py-2.5 text-right text-xs text-emerald-700 font-semibold">{j.joined}</td>
                          <td className="px-2 py-2.5 text-right text-xs text-rose-600 hidden md:table-cell">{j.rejected}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </ScrollArea>
            </Card>
          </div>

          {/* Recent activity */}
          <Card className="p-4" data-testid="rp-activity-card">
            <h2 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-500" />
              Recent Activity
              <span className="text-xs font-mono text-slate-500 ml-auto">{data?.recent_activity?.length || 0}</span>
            </h2>
            {(data?.recent_activity?.length || 0) === 0 ? (
              <div className="text-center py-6 text-sm text-slate-400" data-testid="rp-activity-empty">
                No stage changes recorded in this period.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.recent_activity.map((e, idx) => (
                  <li
                    key={`${e.application_id}-${e.date}-${idx}`}
                    className="py-1.5 flex items-center gap-3"
                    data-testid={`rp-activity-row-${idx}`}
                  >
                    <Badge variant="outline" className="text-[10px] capitalize flex-shrink-0">
                      {e.stage?.replace(/_/g, ' ') || ''}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-slate-800 truncate">{e.candidate_name || 'Unnamed'}</span>
                      <span className="text-xs text-slate-500 ml-2 truncate">· {e.job_title || '—'}</span>
                    </div>
                    {e.remarks ? (
                      <span className="text-[11px] text-slate-400 italic truncate max-w-[200px]" title={e.remarks}>
                        {'\u201c'}{e.remarks}{'\u201d'}
                      </span>
                    ) : null}
                    <span className="text-[10px] text-slate-400 flex-shrink-0">
                      {e.date ? new Date(e.date).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {data?.generated_at && (
            <p className="text-[10px] text-slate-400 text-right">
              Generated {new Date(data.generated_at).toLocaleString()}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default RecruiterPerformance;
